/* eslint-disable*/
import { name } from "./__init__";
import { createSelector } from "reselect";
import range from "lodash/range";
import toLower from "lodash/toLower";
import { selectors as appSelectors } from "../../containers/App";
import { selectors as reservationSelectors } from "../../containers/ReservationManager";
import { fromJS, List, Map } from "immutable";
import { calculateDaysBetweenDates } from "../../utils/date";
import { buildImgUrl, filterDisplayElement, getFromSessionStorage, sortBy } from "../../utils/misc";
import { calculateSelectorsRange } from "../../utils/bookingFormUtils";

import constants, { BOOKING_FORM_NAME_MOUNTPOINT } from "../../utils/constants";
import { buildPropertyList, propertyARISelector } from "../App/selectors";

const selectors = { ...appSelectors, ...reservationSelectors };

export const getModelSelector = state => state[name];

/* **********************************************BOOKING FORM SELECTORS********************************************** */

export const bookingFormSelector = state => state[BOOKING_FORM_NAME_MOUNTPOINT];

const showChildrenCounter = (occupantsMaxValues, occupantsFilterType) =>
  occupantsFilterType === constants.OCCUPANTS_FILTER_TYPE.AGE &&
  (occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER) ||
    occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN) ||
    occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY));

export const guestAgesSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("guestAges")
);

export const hasPremiumMapSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("hasPremiumMap")
);

export const showOccupationAlternativesSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("showOccupationAlternatives")
);

export const originAccommodationSelector = createSelector(
  getModelSelector,
  model => model && model.get("originAccommodation")
);

export const displayElementParamSelector = createSelector(
  selectors.displayElementUrlParamSelector,
  displayElementUrlParam => displayElementUrlParam
);

export const childrenCountSelector = createSelector(
  bookingFormSelector,
  guestAgesSelector,
  (formData, guestAges) =>
    (formData && formData.childrenCount && formData.childrenCount.value) ||
    (guestAges && guestAges.size) ||
    null
);

/* Created only to reduce computations when configuration changes */
export const bookingFormFieldsSelectorInput = createSelector(
  selectors.configurationSelector,
  configuration => {
    if (!configuration) {
      return null;
    }
    return {
      occupantsMaxValues: configuration.get("occupantsMaxValues"),
      occupantsMinAge: configuration.get("occupantsMinAge"),
      occupantsFilterType: configuration.get("occupantsFilterType"),
      showHotelSelector: configuration.get("showHotelSelector"),
      ageRanges: configuration.get("ageRanges"),
    };
  }
);

export const occupantsSelectorRangeSelector = createSelector(
  selectors.configurationSelector,
  configuration =>
    configuration && {
      [constants.OCCUPANTS_TYPE.SENIOR]: configuration.get("seniorSelectorRange"),
      [constants.OCCUPANTS_TYPE.ADULT]: configuration.get("adultSelectorRange"),
      [constants.OCCUPANTS_TYPE.TEENAGER]: configuration.get("teenagerSelectorRange"),
      [constants.OCCUPANTS_TYPE.CHILDREN]: configuration.get("childSelectorRange"),
      [constants.OCCUPANTS_TYPE.BABY]: configuration.get("babySelectorRange"),
    }
);

export const bookingFormFieldsSelector = createSelector(
  bookingFormFieldsSelectorInput,
  childrenCountSelector,
  selectors.configurationSelector,
  occupantsSelectorRangeSelector,
  (bookingFormFieldsInput, childrenCount, configuration, occupantsSelectorRange) => {
    if (!bookingFormFieldsInput) {
      return null;
    }
    const {
      occupantsMaxValues,
      occupantsFilterType,
      occupantsMinAge,
      showHotelSelector,
    } = bookingFormFieldsInput;
    const showChildrenCount = showChildrenCounter(occupantsMaxValues, occupantsFilterType);

    const childrenFields = childrenCount
      ? range(childrenCount).reduce(
          (previous, idx) => ({
            ...previous,
            [`age${idx}`]: true,
          }),
          {}
        )
      : [];

    const fields = {
      startDate: true,
      endDate: true,
      childrenCount: showChildrenCount,
      hotelSelector: showHotelSelector,
      promoCodes: true,
      ...childrenFields,
    };

    switch (configuration.get("occupantRestriction")) {
      case constants.OCCUPANT_RESTRICTION.TAKE_ALL_PEOPLE:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_SENIOR_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.SENIOR] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.SENIOR
        );
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.NONE:
      default:
        break;
    }

    const selectorsRange = calculateSelectorsRange({
      showChildrenCount,
      occupantsMinAge,
      occupantsMaxValues,
      occupantsSelectorRange,
      fields,
    });
    const ageRanges = bookingFormFieldsInput.ageRanges && bookingFormFieldsInput.ageRanges.toJS();
    return { fields, selectorsRange, ageRanges };
  }
);

/* Created only to reduce computations when configuration changes */
export const bookingFormInitialValuesSelectorInput = createSelector(
  selectors.configurationSelector,
  configuration => {
    return (
      configuration && {
        ...constants.OCCUPANTS_TYPE_ORDER.reduce(
          (previous, type) => ({
            ...previous,
            [type]: configuration.get(type),
          }),
          {}
        ),
        guestAges: configuration.get("guestAges"),
        startDate: configuration.get("startDate"),
        endDate: configuration.get("endDate"),
        promoCodes: configuration.get("promoCodes"),
      }
    );
  }
);

export const bookingFormInitialValuesSelector = createSelector(
  bookingFormInitialValuesSelectorInput,
  configurationSubset => {
    if (!configurationSubset) {
      return null;
    }
    const { guestAges, ...rest } = configurationSubset;

    let initialValues = rest;

    if (guestAges && guestAges.size > 0) {
      initialValues = {
        ...initialValues,
        ...guestAges.reduce((previous, age, idx) => ({ ...previous, [`age${idx}`]: age }), {}),
        childrenCount: guestAges.size,
        guestAges: guestAges.toJS(),
      };
    }

    return initialValues;
  }
);

/* **********************************************BOOKING FORM SELECTORS********************************************** */

/* *********************************************ESTABLISHMENT SELECTORS********************************************** */
/* TODO: SORT THEM BASED ON STATUS (AVAILABLE (sorted by rate), RESTRICTED, UNAVAILABLE) , for this NEEDS ARI DATA?? */

export const propertyDataListSelector = createSelector(
  selectors.propertyDataSelector,
  propertyData =>
    (propertyData &&
      propertyData.get("entities") &&
      propertyData.get("entities").get("establishments").toArray()) ||
    []
);

export const activeChildrenMapSelector = createSelector(
  getModelSelector,
  model => model && model.get("activeChildrenMap")
);

export const activeRoomsSelector = createSelector(
  getModelSelector,
  model => model && model.get("activeRooms")
);

export const activePropertyTickerSelector = createSelector(
  getModelSelector,
  model => model && model.get("activePropertyTicker")
);

export const activeEntityTypeSelector = createSelector(
  getModelSelector,
  model => model && model.get("activeEntityType")
);

export const activeEntitiesTypeSelector = createSelector(
  activeEntityTypeSelector,
  activeEntityType => (activeEntityType ? `${activeEntityType}s` : null)
);

export const promoCodesSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("promoCodes")
);

export const promoCodesAppliedSelector = createSelector(
  selectors.propertyARISelector,
  selectors.propertyTickerSelector,
  (propertyARI, propertyTicker) => {
    return (
      propertyARI.get(propertyTicker) &&
      propertyARI.get(propertyTicker).get("validPromoCodes") &&
      propertyARI.get(propertyTicker).get("validPromoCodes").toSet()
    );
  }
);

export const arePromoCodesBeingAppliedSelector = createSelector(
  selectors.configurationSelector,
  promoCodesAppliedSelector,
  (configuration, validatedPromoCodes) => {
    const submittedPromoCodes =
      configuration &&
      configuration.get("promoCodes") &&
      fromJS(configuration.get("promoCodes").replaceAll("ñ", "n").split(",")).toSet();
    if (!submittedPromoCodes) {
      return null;
    }
    if (submittedPromoCodes && validatedPromoCodes) {
      const newValidatedPromoCodes = submittedPromoCodes
        .map(toLower)
        .intersect(validatedPromoCodes.map(toLower));
      return newValidatedPromoCodes.size > 0;
    }
    return false;
  }
);

function formatProperty(
  property,
  {
    numberOfNights,
    currency,
    imgHeight,
    imgWidth,
    imgPlaceHolder,
    type,
    ariValues,
    loyalty,
    hasCompoundDiscounts,
    obligatoryExtrasAmount,
    nights,
  }
) {
  const media = property.get("media") ? property.get("media").toJS() : [];
  const lastStatus = property.get("lastStatus") || null;
  const loyaltyPromotedDiscount = loyalty && loyalty.promotedDiscount;

  const propertyObligatoryExtrasAmount =
    obligatoryExtrasAmount && obligatoryExtrasAmount.get(property.get("ticker"))
      ? obligatoryExtrasAmount.get(property.get("ticker"))
      : 0;
  return {
    ticker: property.get("ticker"),
    title: property.get("name"),
    subtitle: "",
    minPrice: property.get("totalRate") / numberOfNights,
    maxPrice: property.get("totalRate") / numberOfNights,
    loyaltyRate: calculateLoyaltyRate({
      originalRate: property.get("originalRate") + propertyObligatoryExtrasAmount,
      totalRate: property.get("totalRate") + propertyObligatoryExtrasAmount,
      loyaltyPromotedDiscount,
      hasCompoundDiscounts,
      obligatoryExtrasAmount: propertyObligatoryExtrasAmount,
      nights,
    }),
    currency: currency,
    thumbnail:
      media && media.length > 0
        ? buildImgUrl(media[0].url, imgHeight, imgWidth, imgPlaceHolder)
        : "https://placehold.it/150x90",
    media:
      media && media.length > 0
        ? media.map(img => ({
            ...img,
            url: img.url,
            description: property.get("description"),
          }))
        : [],
    description: property.get("description"),
    inventories: (property.get("inventories") && property.get("inventories").toJS()) || [],
    statusCode: (lastStatus && lastStatus.get("code")) || null,
    statusValue: (lastStatus && lastStatus.get("value")) || null,
    statusFilterType: (lastStatus && lastStatus.get("filterType")) || null,
    order: property.get("order"),
    type,
  };
}

function propertiesValidation(properties, { accommodationsOrder }) {
  if (properties && properties.size > 0) {
    let available = properties.filter(
      property => property.statusCode === constants.PROPERTY_STATUS.AVAILABLE
    );
    let restricted = properties.filter(
      property => property.statusCode === constants.PROPERTY_STATUS.RESTRICTED
    );
    let unavailable = properties.filter(
      property => property.statusCode === constants.PROPERTY_STATUS.UNAVAILABLE
    );

    if (available) {
      if (accommodationsOrder === constants.ACCOMMODATIONS_ORDER.LIST_POSITION) {
        available = available.sort((itemA, itemB) => sortBy(itemA, itemB, "order", false));
      } else {
        available = available.sort((itemA, itemB) => sortBy(itemA, itemB, "minPrice", false));
      }
    }
    if (restricted) {
      restricted = restricted.sort((itemA, itemB) => sortBy(itemA, itemB, "order", false));
    }
    if (unavailable) {
      unavailable = unavailable.sort((itemA, itemB) => sortBy(itemA, itemB, "order", false));
    }

    return available.concat(restricted).concat(unavailable) || new List();
  }
  return new List();
}

export const numberOfNightsSelector = createSelector(
  selectors.startDateSelector,
  selectors.endDateSelector,
  (startDate, endDate) => {
    return calculateDaysBetweenDates({ startDate, endDate });
  }
);

function setFormatAccommodations(
  propertyList,
  accommodationsOrder,
  currency,
  numberOfNights,
  fullWidth,
  loyalty,
  hasCompoundDiscounts,
  obligatoryExtrasAmount,
  nights
) {
  let results = new Map();
  const imgWidth = (fullWidth && window && window.innerWidth >= 768 && 750) || 194;
  const imgHeight = (fullWidth && window && window.innerWidth >= 768 && 500) || 129;

  const imgPlaceHolder =
    (fullWidth && "https://placehold.it/252x159") || "https://placehold.it/135x90";
  propertyList.forEach(property => {
    const accommodations =
      (property.get("accommodations") &&
        property
          .get("accommodations")
          .map(it =>
            formatProperty(it, {
              numberOfNights,
              currency,
              imgHeight,
              imgWidth,
              imgPlaceHolder,
              type: "accommodation",
              ariValues:
                property.get("arivalues") &&
                property.get("arivalues").get(`accommodation_${it.get("ticker")}`),
              loyalty,
              hasCompoundDiscounts,
              obligatoryExtrasAmount,
              nights,
            })
          )
          .toList()) ||
      [];

    const packages =
      (property.get("packages") &&
        property
          .get("packages")
          .map(it =>
            formatProperty(it, {
              numberOfNights,
              currency,
              imgHeight,
              imgWidth,
              imgPlaceHolder,
              type: "package",
              ariValues:
                property.get("arivalues") &&
                property.get("arivalues").get(`package_${it.get("ticker")}`),
              loyalty,
              hasCompoundDiscounts,
              obligatoryExtrasAmount,
              nights,
            })
          )
          .toList()) ||
      [];
    const allProperties = accommodations.size
      ? accommodations.concat(packages)
      : packages.concat(accommodations);
    const formattedProperties = propertiesValidation(allProperties, {
      accommodationsOrder,
    });
    results = results.set(property.get("ticker"), formattedProperties);
  });
  return results;
}

function getInventoriesObligatoryExtrasRate({ ariValues, inventories, services }) {
  if (!inventories || !services) {
    return new Map();
  }
  return inventories.map(it => {
    if (!it.get("services")) {
      return 0;
    }
    return it
      .get("services")
      .filter(it => services.get(it) && services.get(it).get("obligatory"))
      .reduce(
        (accumulator, it) =>
          ariValues.get(it) && ariValues.get(it).get("totalRate")
            ? ariValues.get(it).get("totalRate") + accumulator
            : accumulator,
        0
      );
  });
}

function getBookablesObligatoryExtrasRate({ ariValues, bookables, inventories }) {
  if (!bookables || !inventories) {
    return new Map();
  }
  return bookables.map(
    it =>
      it &&
      it.get("inventories") &&
      it.get("inventories").reduce((a, b) => {
        if (!ariValues.get(b)) {
          return a;
        }
        const extrasRate = inventories.get(b) || a;
        return a && a < extrasRate ? a : extrasRate;
      }, null)
  );
}

export const obligatoryExtrasAmountSelector = createSelector(
  selectors.loyaltySelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  propertyARISelector,
  selectors.propertyTickerSelector,
  (loyalty, addObligatoryExtrasRateToRoomSelection, propertyARI, propertyTicker) => {
    if (!loyalty || !addObligatoryExtrasRateToRoomSelection) {
      return new Map();
    }
    if (
      propertyARI &&
      propertyARI.get(propertyTicker) &&
      propertyARI.get(propertyTicker).get("arivalues")
    ) {
      const ariValues = propertyARI.get(propertyTicker).get("arivalues");
      const inventories = getInventoriesObligatoryExtrasRate({
        ariValues,
        inventories: propertyARI.get(propertyTicker).get("inventories"),
        services: propertyARI.get(propertyTicker).get("services"),
      });
      const accommodations = getBookablesObligatoryExtrasRate({
        ariValues,
        bookables: propertyARI.get(propertyTicker).get("accommodations"),
        inventories,
      });
      const packages = getBookablesObligatoryExtrasRate({
        ariValues,
        bookables: propertyARI.get(propertyTicker).get("packages"),
        inventories,
      });
      return inventories.merge(accommodations).merge(packages);
    }
    return new Map();
  }
);

export const accommodationsSelector = createSelector(
  selectors.propertyListSelector,
  selectors.accommodationsOrderSelector,
  selectors.currencySelector,
  numberOfNightsSelector,
  selectors.fullWidthSelector,
  selectors.loyaltySelector,
  selectors.compoundDiscountsSelector,
  obligatoryExtrasAmountSelector,
  numberOfNightsSelector,
  (
    propertyList,
    accommodationsOrder,
    currency,
    numberOfNights,
    fullWidth,
    loyalty,
    hasCompoundDiscounts,
    obligatoryExtrasAmount,
    nights
  ) => {
    return setFormatAccommodations(
      propertyList,
      accommodationsOrder,
      currency,
      numberOfNights,
      fullWidth,
      loyalty,
      hasCompoundDiscounts,
      obligatoryExtrasAmount,
      nights
    );
  }
);

export const propertyListAllSelector = createSelector(
  selectors.propertyARIAllSelector,
  selectors.propertyDataSelector,
  reservationSelectors.reservationsSelector,
  selectors.startDateSelector,
  selectors.endDateSelector,
  selectors.currencySelector,
  selectors.defaultCurrencySelector,
  selectors.currencyExchangeSelector,
  selectors.propertyTickerSelector,
  selectors.isPropertyChainSelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  selectors.propertyTabsSelector,
  selectors.configurationSelector,
  selectors.taxesPercentageSelector,
  selectors.propertyActiveTabSelector,
  buildPropertyList
);

const calculateLoyaltyRate = ({
  originalRate,
  totalRate,
  loyaltyPromotedDiscount,
  hasCompoundDiscounts,
  obligatoryExtrasAmount = 0,
  nights,
}) => {
  if (!loyaltyPromotedDiscount) {
    return nights ? originalRate / nights : originalRate;
  }
  const loyaltyDiscountRate = (loyaltyPromotedDiscount && loyaltyPromotedDiscount.amount) || 0;
  if (!loyaltyDiscountRate) {
    return nights ? originalRate / nights : originalRate;
  }
  const originalRateWithoutObligatoryExtras = originalRate - obligatoryExtrasAmount;
  const totalRateWithoutObligatoryExtras = totalRate - obligatoryExtrasAmount;
  let loyaltyRate;
  const discountPercentage =
    1 - totalRateWithoutObligatoryExtras / originalRateWithoutObligatoryExtras;
  switch (loyaltyPromotedDiscount.type) {
    case constants.PROMOTED_DISCOUNT_TYPE.PERCENTAGE:
      loyaltyRate =
        originalRateWithoutObligatoryExtras * (1 - (discountPercentage + loyaltyDiscountRate));
      if (hasCompoundDiscounts) {
        loyaltyRate =
          originalRateWithoutObligatoryExtras *
          (1 - discountPercentage) *
          (1 - loyaltyDiscountRate);
      }
      break;

    case constants.PROMOTED_DISCOUNT_TYPE.CONSTANT:
      const calculatedLoyaltyDiscount =
        (originalRateWithoutObligatoryExtras * loyaltyDiscountRate) / 100;
      loyaltyRate =
        originalRateWithoutObligatoryExtras *
        (1 - (discountPercentage + calculatedLoyaltyDiscount));
      if (hasCompoundDiscounts) {
        loyaltyRate =
          originalRateWithoutObligatoryExtras *
          (1 - discountPercentage) *
          (1 - calculatedLoyaltyDiscount);
      }
      break;

    default:
      loyaltyRate = originalRateWithoutObligatoryExtras;
      break;
  }
  return nights
    ? (loyaltyRate + obligatoryExtrasAmount) / nights
    : loyaltyRate + obligatoryExtrasAmount;
};

export const inventoriesSelector = createSelector(
  selectors.propertyListSelector,
  propertyListAllSelector,
  originAccommodationSelector,
  activeRoomsSelector,
  activeEntitiesTypeSelector,
  selectors.compoundDiscountsSelector,
  selectors.loyaltySelector,
  obligatoryExtrasAmountSelector,
  (
    propertyList,
    propertyListAll,
    originAccommodationSelector,
    activeRoomsMap,
    activeEntitiesType,
    hasCompoundDiscounts,
    loyalty,
    obligatoryExtrasAmount
  ) => {
    const propertyListSelected =
      originAccommodationSelector === constants.OCCUPATION_ALTERNATIVES
        ? propertyListAll
        : propertyList;
    let inventoryMap = new Map();
    if (!activeRoomsMap) {
      return null;
    }
    let propertyMap = new Map();
    propertyListSelected.forEach(property => {
      propertyMap = propertyMap.set(property.get("ticker"), property);
    });
    activeRoomsMap.entrySeq().forEach(([propertyTicker, activeRoom]) => {
      const property = propertyMap.get(propertyTicker);
      /** TODO: Cuando se selecciona una habitación en un agregador se filtra el propertyList dejando únicamente el hotel seleccionado.
       * El problema es que si en activeRooms hay más de una habitación activa se intentan renderizar los inventarios de un hotel que
       * ya no existe puesto que fueron filtrados del propertyList. Lo ideal sería no renderizar el inventoryList al seleccionar una
       * habitación e ir al step2.
       * **/
      if (!property) {
        return;
      }

      inventoryMap.set(property.get("ticker"), []);
      if (!activeRoom || !activeEntitiesType) {
        return;
      }
      if (!property.get(activeEntitiesType) || !property.get(activeEntitiesType).get(activeRoom)) {
        return;
      }

      const roomDenomination = property
        .get(activeEntitiesType)
        .get(activeRoom)
        .get("roomDenomination");

      const inventoryList = new List(
        property
          .get(activeEntitiesType)
          .get(activeRoom)
          .get("inventories")
          .map(it => {
            const inventory = property.get("inventories").get(it);
            if (!property.get("arivalues").get(it)) {
              return null;
            }
            const lastStatus = inventory.get("lastStatus") || null;
            let discountAmount = inventory.get("discountAmount");
            let discountPercentage = inventory.get("discountPercentage");
            if (inventory.get("rackRate") > 0) {
              discountAmount = inventory.get("rackRate") - inventory.get("totalRate");
              discountAmount = (discountAmount > 0 && discountAmount) || null;
              discountPercentage = 1 - inventory.get("totalRate") / inventory.get("rackRate");
            }
            const loyaltyPromotedDiscount = loyalty && loyalty.promotedDiscount;
            const loyaltyRate = calculateLoyaltyRate({
              originalRate: inventory.get("originalRate"),
              totalRate: inventory.get("totalRate"),
              loyaltyPromotedDiscount,
              hasCompoundDiscounts,
              obligatoryExtrasAmount:
                obligatoryExtrasAmount && obligatoryExtrasAmount.get(inventory.get("ticker")),
            });
            return new Map({
              ticker: it,
              accommodation: activeRoom,
              mealplan: property.get("mealplans").get(inventory.get("mealplan")),
              condition: property.get("conditions").get(inventory.get("condition")),
              rackRate: inventory.get("rackRate") || null,
              paymentTypes:
                property.get("conditions").get(inventory.get("condition")).get("paymentTypes") ||
                null,
              discountPercentage: discountPercentage || null,
              discountAmount: discountAmount || null,
              originalRate: inventory.get("originalRate") || null,
              averageRate: inventory.get("averageRate") || null,
              totalRate: inventory.get("totalRate") || null,
              loyaltyRate,
              availability: inventory.get("availability") || null,
              configuration: property.get("configurations").get(inventory.get("configuration")),
              statusCode: (lastStatus && lastStatus.get("code")) || null,
              statusValue: (lastStatus && lastStatus.get("value")) || null,
              statusFilterType: (lastStatus && lastStatus.get("filterType")) || null,
              originalRates: inventory.get("originalRates"),
              services: inventory.get("services") || null,
              roomDenomination:
                roomDenomination || roomDenomination || constants.ROOM_DENOMINATION.UNITS,
              bookingOption: inventory.get("bookingOption"),
            });
          })
          .filter(it => it !== null)
          .sort((itemA, itemB) => sortBy(itemA, itemB, "ticker", false))
          .sort((itemA, itemB) => sortBy(itemA, itemB, "totalRate", false))
          .sort((itemA, itemB) => {
            if (
              (itemA.get("statusCode") === constants.BOOKABLE_STATUS.AVAILABLE &&
                itemB.get("statusCode") !== constants.BOOKABLE_STATUS.AVAILABLE) ||
              (itemA.get("statusCode") === constants.BOOKABLE_STATUS.RESTRICTED &&
                itemB.get("statusCode") === constants.BOOKABLE_STATUS.UNAVAILABLE)
            ) {
              return -1;
            }
            return 0;
          })
      );
      inventoryMap = inventoryMap.set(property.get("ticker"), inventoryList);
    });
    return inventoryMap;
  }
);

function getActiveFullRoomPropertyTicker(activeRooms) {
  let activePropertyTicker;
  const activeRoom = activeRooms.filter(accommodation => accommodation);

  if (activeRoom && activeRoom.size > 0) {
    activePropertyTicker = activeRoom.keys().next().value;
  }

  return activePropertyTicker;
}

export const activePropertyARIFullRoomSelector = createSelector(
  selectors.propertyARIAllSelector,
  selectors.propertyARISelector,
  activeRoomsSelector,
  showOccupationAlternativesSelector,
  (propertyARIAll, propertyARI, activeRooms, showOccupationAlternatives) => {
    const activePropertyTicker = getActiveFullRoomPropertyTicker(activeRooms);
    const ARI =
      showOccupationAlternatives &&
      showOccupationAlternatives !== constants.SHOW_OCCUPATION_ALTERNATIVES.HIDE &&
      propertyARIAll
        ? propertyARIAll
        : propertyARI;
    return ARI && ARI.get(activePropertyTicker);
  }
);

export const activePropertyDataFullRoomSelector = createSelector(
  selectors.propertyDataSelector,
  activeRoomsSelector,
  (propertyData, activeRooms) => {
    const activePropertyTicker = getActiveFullRoomPropertyTicker(activeRooms);

    return propertyData && propertyData.get(activePropertyTicker);
  }
);

export const summaryCartSelector = createSelector(selectors.cartSelector, cart => {
  const result = { reservations: [] };

  if (cart && cart.reservations) {
    result.totalAmount = cart.bookingTotalRate;

    cart.reservations.map(reservation => {
      const inventory = reservation.get("inventory");
      result.reservations.push({
        id: reservation.get("id"),
        accommodationTicker:
          inventory &&
          inventory.get("accommodation") &&
          inventory.get("accommodation").get("ticker"),
        accommodationName:
          inventory && inventory.get("accommodation") && inventory.get("accommodation").get("name"),
        quantity: reservation.get("numberOfUnits"),
        startDate: reservation.get("startDate"),
        endDate: reservation.get("endDate"),
        configuration:
          inventory && inventory.get("configuration") && inventory.get("configuration").get("name"),
        condition:
          inventory && inventory.get("condition") && inventory.get("condition").get("name"),
        mealPlan: inventory && inventory.get("mealplan") && inventory.get("mealplan").get("name"),
        totalRate: reservation.get("totalRate"),
        currency: inventory && inventory.get("currency"),
      });
    });
  }

  return result;
});

export const propertySelectionBehaviorSelector = createSelector(
  selectors.configurationSelector,
  selectors.isPropertyChainSelector,
  (configuration, isPropertyChain) => {
    if (!isPropertyChain || window.self !== window.top) {
      return null;
    }

    return configuration && configuration.get("propertySelectionBehavior");
  }
);

export const accommodationSelectionBehaviorSelector = createSelector(
  selectors.configurationSelector,
  selectors.isPropertyChainSelector,
  (configuration, isPropertyChain) => {
    if (!isPropertyChain || window.self !== window.top) {
      return null;
    }

    return configuration && configuration.get("accommodationSelectionBehavior");
  }
);

export const openCalendarSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("openCalendar")
);

export const customDomainsSelector = createSelector(
  selectors.propertyDataSelector,
  propertyData => {
    const customDomains = {};

    if (propertyData) {
      propertyData.map((data, propertyTicker) => {
        customDomains[propertyTicker] = data && data.get("customDomain");
      });
    }

    return customDomains;
  }
);

export const activeEntitySelector = createSelector(
  activePropertyTickerSelector,
  activeRoomsSelector,
  activePropertyARIFullRoomSelector,
  selectors.propertyDataSelector,
  activeEntityTypeSelector,
  (
    activePropertyTicker,
    activeRooms,
    activePropertyARIFullRoom,
    propertyData,
    activeEntityType
  ) => {
    if (!activePropertyTicker) {
      return null;
    }
    const selectedRoom =
      activeRooms && activeRooms.size > 0 && activeRooms.get(activePropertyTicker);
    let activeEntity = propertyData && propertyData.get(activePropertyTicker);
    let type = activeEntity.get("children")
      ? constants.ACTIVE_ENTITY_TYPE.CHAIN
      : constants.ACTIVE_ENTITY_TYPE.HOTEL;
    if (selectedRoom && activePropertyARIFullRoom) {
      if (activeEntityType === "package") {
        type = constants.ACTIVE_ENTITY_TYPE.PACKAGE;
        activeEntity = activePropertyARIFullRoom.get("packages").get(selectedRoom);
      } else {
        type = constants.ACTIVE_ENTITY_TYPE.ACCOMMODATION;
        activeEntity = activePropertyARIFullRoom.get("accommodations").get(selectedRoom);
      }
    }
    return activeEntity && { ...activeEntity.toJS(), type };
  }
);

export const showCalendarSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("showCalendar")
);

export const showCalendarByUrlSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("showCalendarByUrl")
);

export const showPropertyMapSelector = createSelector(
  selectors.configurationSelector,
  configuration => configuration && configuration.get("showPropertyMap")
);

// returns list of all display elements including, default ones
// WITHOUT filters
export const displayElementsSelector = createSelector(
  activePropertyTickerSelector,
  selectors.propertyDataSelector,
  selectors.isPropertyChainSelector,
  showCalendarSelector,
  showCalendarByUrlSelector,
  showPropertyMapSelector,
  (
    activePropertyTicker,
    propertyData,
    isPropertyChain,
    showCalendar,
    showCalendarByUrl,
    showPropertyMap
  ) => {
    if (!activePropertyTicker && !isPropertyChain) {
      return null;
    }
    let customDisplayElements =
      propertyData &&
      propertyData.get(activePropertyTicker) &&
      propertyData.get(activePropertyTicker).get("display");

    customDisplayElements =
      customDisplayElements && customDisplayElements.size > 0 ? customDisplayElements.toJS() : [];

    const defaultInfoElement = {
      active: true,
      ticker: "info",
      type: "INFO",
      label: constants.DISPLAY_ELEMENT_TYPE.INFO,
      content: null,
      position: 0,
      conditions: [
        {
          class: "stringComparison",
          value: [
            constants.ACTIVE_ENTITY_TYPE.HOTEL,
            constants.ACTIVE_ENTITY_TYPE.ACCOMMODATION,
            constants.ACTIVE_ENTITY_TYPE.PACKAGE,
          ],
          type: "ENTITY",
          modifier: "INCLUDE",
        },
      ],
    };
    // this is the way to override the default elements
    // by using the same ticker
    const infoElement =
      customDisplayElements.find(elem => elem.ticker === "info") || defaultInfoElement;
    // we hardcode the position so it appears as the first one
    infoElement.position = 0;

    const defaultMapElement = {
      active: true,
      ticker: "map",
      type: "MAP",
      label: constants.DISPLAY_ELEMENT_TYPE.MAP,
      position: 0,
      conditions: [
        {
          class: "stringComparison",
          value: [constants.ACTIVE_ENTITY_TYPE.HOTEL, constants.ACTIVE_ENTITY_TYPE.CHAIN],
          type: "ENTITY",
          modifier: "INCLUDE",
        },
      ],
    };

    const mapElement =
      customDisplayElements.find(elem => elem.active && elem.ticker === "map") || defaultMapElement;
    mapElement.position = 0;

    let availabilityElement = null;
    if (showCalendar || showCalendarByUrl) {
      const defaultAvailabilityElement = {
        active: true,
        ticker: "availability",
        type: "AVAILABILITY",
        label: constants.DISPLAY_ELEMENT_TYPE.AVAILABILITY,
        content: null,
        position: 0,
        conditions: [
          {
            class: "stringComparison",
            value: [constants.ACTIVE_ENTITY_TYPE.HOTEL],
            type: "ENTITY",
            modifier: "INCLUDE",
          },
        ],
      };

      availabilityElement =
        customDisplayElements.find(elem => elem.ticker === "availability") ||
        defaultAvailabilityElement;
      availabilityElement.position = 0;
    }

    const defaultDisplayElements = showPropertyMap ? [infoElement, mapElement] : [infoElement];

    if (availabilityElement) {
      defaultDisplayElements.push(availabilityElement);
    }

    return [
      ...defaultDisplayElements,
      ...customDisplayElements.filter(
        elem => elem.ticker !== "availability" && elem.ticker !== "info" && elem.ticker !== "map"
      ),
    ];
  }
);
//returns all filtered displayElements by active, and booking window
export const filteredDisplayElementsSelector = createSelector(
  displayElementsSelector,
  activeEntitySelector,
  selectors.isPropertyChainSelector,
  (allDisplayElements, activeEntity, isPropertyChain) => {
    if (!allDisplayElements || (!activeEntity && !isPropertyChain)) {
      return [];
    }
    const activeFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.ACTIVE,
    });

    const bookingWindowFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.BOOKING_WINDOW,
    });

    const infoByContentFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: "info_content",
    });

    return allDisplayElements
      .filter(infoByContentFilter)
      .filter(activeFilter)
      .filter(bookingWindowFilter)
      .sort((a, b) => (a.position === b.position ? 0 : +(a.position > b.position) || -1));
  }
);

export const activeDisplayElementStateSelector = createSelector(
  getModelSelector,
  model => model && model.get("activeDisplayElement")
);

// given the active entity present
// filter the available display elements
export const displayActiveElementsSelector = createSelector(
  displayElementsSelector,
  activeEntitySelector,
  selectors.isPropertyChainSelector,
  (allDisplayElements, activeEntity, isPropertyChain) => {
    if (!allDisplayElements || (!activeEntity && !isPropertyChain)) {
      return null;
    }
    const activeFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.ACTIVE,
    });

    const entityFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.ENTITY,
    });

    const tickerFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.TICKER,
    });

    const bookingWindowFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: constants.DISPLAY_CONDITION_TYPE.BOOKING_WINDOW,
    });

    const infoByContentFilter = filterDisplayElement({
      activeEntity,
      isPropertyChain,
      type: "info_content",
    });

    return allDisplayElements
      .filter(infoByContentFilter)
      .filter(entityFilter)
      .filter(tickerFilter)
      .filter(activeFilter)
      .filter(bookingWindowFilter)
      .sort((a, b) => (a.position === b.position ? 0 : +(a.position > b.position) || -1));
  }
);

export const activeDisplayElementSelector = createSelector(
  activeDisplayElementStateSelector,
  displayActiveElementsSelector,
  selectors.isDisplayActiveSelector,
  (activeDisplayElementState, displayActiveElements, isDisplayActive) => {
    if (
      !activeDisplayElementState &&
      (!isDisplayActive || !displayActiveElements || displayActiveElements.length === 0)
    ) {
      return null;
    }

    if (!displayActiveElements) {
      return null;
    }

    // if there's no active display element, we show the first element
    if (!activeDisplayElementState) {
      const defaultActiveDisplayElement = displayActiveElements.find(
        element => element.defaultOption
      );

      return defaultActiveDisplayElement || displayActiveElements[0];
    }
    return (
      displayActiveElements.find(elem => elem.ticker === activeDisplayElementState) ||
      (isDisplayActive && displayActiveElements[0]) ||
      null
    );
  }
);

export const renderDisplayElementAsModalSelector = createSelector(
  getModelSelector,
  model => model && model.get("renderDisplayElementAsModal")
);

export const allAccommodationsSelector = createSelector(
  propertyListAllSelector,
  selectors.propertyListSelector,
  selectors.accommodationsOrderSelector,
  selectors.currencySelector,
  numberOfNightsSelector,
  selectors.fullWidthSelector,
  showOccupationAlternativesSelector,
  selectors.loyaltySelector,
  selectors.compoundDiscountsSelector,
  obligatoryExtrasAmountSelector,
  numberOfNightsSelector,
  (
    propertyListAll,
    propertyList,
    accommodationsOrder,
    currency,
    numberOfNights,
    fullWidth,
    showOccupationAlternatives,
    loyalty,
    hasCompoundDiscounts,
    obligatoryExtrasAmount,
    nights
  ) => {
    return setFormatAccommodations(
      showOccupationAlternatives &&
        showOccupationAlternatives !== constants.SHOW_OCCUPATION_ALTERNATIVES.HIDE
        ? propertyListAll
        : propertyList,
      accommodationsOrder,
      currency,
      numberOfNights,
      fullWidth,
      loyalty,
      hasCompoundDiscounts,
      obligatoryExtrasAmount,
      nights
    );
  }
);

export const bookableCardSizeSelector = createSelector(
  selectors.isDisplayActiveSelector,
  selectors.isPropertyChainSelector,
  (isDisplayActive, isPropertyChainSelector) =>
    isDisplayActive && !isPropertyChainSelector
      ? constants.HOTEL_CARD_SIZE.SMALL
      : constants.HOTEL_CARD_SIZE.LARGE
);

export const resultHeaderNatureUISelector = createSelector(selectors.widthSelector, width => {
  if (width <= 767) {
    return constants.RESULT_HEADER_NATURE.SMALL;
  } else if (width <= 1279) {
    return constants.RESULT_HEADER_NATURE.MEDIUM;
  } else if (width >= 1280) {
    return constants.RESULT_HEADER_NATURE.LARGE;
  }
});

export const isButtonBarActiveSelector = createSelector(
  selectors.isDisplayActiveSelector,
  isDisplayActive => !isDisplayActive
);

function isPromotionApplied({
  showInactiveDiscounts,
  inventories,
  promotion,
  selectedInventories,
  activePropertyTicker,
  showRestrictedHighlightMessages,
  ariValues,
}) {
  const isHighlight = !promotion.get("reduction");
  // if configuration marks discount as always shown, we don't care about anything else, it will always be visisble.
  const discountMustAlwaysBeShown = isHighlight
    ? showRestrictedHighlightMessages
    : showInactiveDiscounts;
  if (discountMustAlwaysBeShown) {
    return true;
  }
  // In this case is an AGR
  if (!inventories || !inventories.size) {
    // Highlight discounts MIGHT not be assigned to any inventory, so we just return them
    if (isHighlight) {
      return true;
    }
    // if there are no inventories available to show and isn't a highlight, we mark the promo as not being applied
    return false;
  }
  const inventoryMap = inventories.toJS() || {};
  // if room has been selected this variable is populated with a list of Inventories
  const currentSelectedInventoriesTickers =
    selectedInventories &&
    selectedInventories.toJS()[activePropertyTicker] &&
    selectedInventories.toJS()[activePropertyTicker].map(it => it.ticker);
  if (!currentSelectedInventoriesTickers) {
    // Highlight discounts MIGHT not be assigned to any inventory, so we just return them
    if (isHighlight) {
      return true;
    }
    // At this point, before any room is selected we just need to check if the promo is available for any inventory
    return Object.values(inventoryMap).some(inventory => {
      return (
        inventory.discountTickers &&
        inventory.discountTickers.includes(promotion.get("ticker")) &&
        ariValues.get(inventory.ticker) &&
        ariValues.get(inventory.ticker).get("discountsApplied") &&
        ariValues.get(inventory.ticker).get("discountsApplied").includes(promotion.get("ticker"))
      );
    });
  }
  // Once the room is selected we just need to check if the promo is available for any CURRENT inventory
  return Object.values(inventoryMap)
    .filter(inventory => currentSelectedInventoriesTickers.includes(inventory.ticker))
    .some(inventory => {
      const isDiscountApplied =
        ariValues.get(inventory.ticker) &&
        ariValues.get(inventory.ticker).get("discountsApplied") &&
        ariValues.get(inventory.ticker).get("discountsApplied").includes(promotion.get("ticker"));
      const showDiscount = isDiscountApplied || isHighlight;
      return (
        inventory.discountTickers &&
        inventory.discountTickers.includes(promotion.get("ticker")) &&
        showDiscount
      );
    });
}

export const propertyPromotionsImmutableSelector = createSelector(
  selectors.propertyTickerSelector,
  selectors.promotionsSelector,
  selectors.propertyARISelector,
  selectors.showInactiveDiscountsSelector,
  selectors.showRestrictedHighlightMessagesSelector,
  inventoriesSelector,
  activePropertyTickerSelector,
  (
    propertyTicker,
    promotions,
    propertyARI,
    showInactiveDiscounts,
    showRestrictedHighlightMessages,
    selectedInventories,
    activePropertyTicker
  ) => {
    const inventories =
      propertyARI &&
      propertyARI.get(propertyTicker) &&
      propertyARI.get(propertyTicker).get("inventories");
    const ariValues = propertyARI && propertyARI.get(propertyTicker).get("arivalues");

    return (
      (promotions &&
        promotions.get(propertyTicker) &&
        promotions
          .get(propertyTicker)
          .filter(promotion => {
            let isApplied = isPromotionApplied({
              showInactiveDiscounts,
              inventories,
              promotion,
              selectedInventories,
              activePropertyTicker,
              showRestrictedHighlightMessages,
              ariValues,
            });

            if (isApplied && promotion.get("visibleInEngine")) {
              return fromJS({
                id: promotion.get("ticker"),
                ticker: promotion.get("ticker"),
                name: promotion.get("name"),
                description: promotion.get("description"),
                reduction: promotion.get("reduction"),
                percentage: promotion.get("percentage"),
                startValidPeriod: new Date(promotion.get("validSince")),
                endValidPeriod: new Date(promotion.get("validUntil")),
                expires: promotion.get("expires"),
                status: promotion.get("status"),
                order: promotion.get("order"),
              });
            }
          })
          .toList()
          .sort((itemA, itemB) => sortBy(itemA, itemB, "order", false))) ||
      undefined
    );
  }
);

export const propertyPromotionsSelector = createSelector(
  propertyPromotionsImmutableSelector,
  propertyPromotionsImmutable => propertyPromotionsImmutable && propertyPromotionsImmutable.toJS()
);

export const isOccupationIgnoredSelector = createSelector(
  selectors.propertyARISelector,
  activePropertyTickerSelector,
  (propertyARI, propertyTicker) => {
    if (propertyARI && propertyARI.size > 0) {
      const occupationStatus =
        propertyARI.get(propertyTicker) &&
        propertyARI.get(propertyTicker).get("status") &&
        propertyARI.get(propertyTicker).get("status").get(constants.ARI_VALUE_TYPE.OCCUPATION);
      return (
        occupationStatus &&
        occupationStatus.get("code") === constants.BOOKABLE_STATUS.IGNORED_FILTER
      );
    }
    return false;
  }
);

export const showUnavailableModalMessageSelector = createSelector(
  selectors.messagesSelector,
  selectors.propertyTickerSelector,
  (messages, propertyTicker) => {
    if (
      messages &&
      messages.get(constants.MESSAGE_POSITIONS.MODAL_STEP_1) &&
      messages.get(constants.MESSAGE_POSITIONS.MODAL_STEP_1).get(0) &&
      messages.get(constants.MESSAGE_POSITIONS.MODAL_STEP_1).get(0).get("showOnUnavailable")
    ) {
      const sessionStorageInfo = getFromSessionStorage(
        constants.LOCAL_STORAGE_KEYS.SHOW_ON_UNAVAILABLE
      );

      const showOnUnavailable = (sessionStorageInfo && JSON.parse(sessionStorageInfo)) || {};

      return !showOnUnavailable[propertyTicker];
    }
    return false;
  }
);

export const roomSelectionViewTypeSelector = createSelector(
  selectors.configurationSelector,
  selectors.deviceInfoSelector,
  (configuration, deviceInfo) => {
    if (deviceInfo.get("browserName") === "Internet Explorer") {
      return constants.ROOM_SELECTION_VIEW_TYPE.LIST;
    } else {
      return configuration && configuration.get("roomSelectionViewType");
    }
  }
);

export const isEntityGridSelector = createSelector(
  roomSelectionViewTypeSelector,
  selectors.widthSelector,
  (roomSelectionViewType, windowWidth) =>
    roomSelectionViewType === constants.ROOM_SELECTION_VIEW_TYPE.GRID ||
    (roomSelectionViewType === constants.ROOM_SELECTION_VIEW_TYPE.GRID_MOBILE && windowWidth < 1280)
);

export const isCartMaxItemsReachedSelector = createSelector(
  selectors.maxCartItemsSelector,
  selectors.cartSelector,
  (maxCartItems, cart) => {
    const cartItems =
      (cart &&
        cart.reservations &&
        cart.reservations.size &&
        cart.reservations.reduce((acc, it) => acc + it.get("numberOfUnits"), 0)) ||
      0;
    return maxCartItems !== 0 && maxCartItems <= cartItems;
  }
);

export const showPromoCodesAppliedPopupDescriptionsSelector = createSelector(
  selectors.propertyARISelector,
  selectors.propertyTickerSelector,
  promoCodesAppliedSelector,
  (propertyARI, propertyTicker, validatedPromoCodes) => {
    if (validatedPromoCodes.length <= 0) {
      return [];
    }
    const discountDescriptionMap = new Map().asMutable();
    const discounts =
      propertyARI.get(propertyTicker) && propertyARI.get(propertyTicker).get("discounts");
    discounts.forEach((discount, promoCode) => {
      discount.get("status").forEach(status => {
        const isAvailable = status.get("code") === "AVAILABLE";
        const isShowableInPopup = discount.get("showPopup");
        if (isAvailable && isShowableInPopup && List.isList(status.get("value"))) {
          discountDescriptionMap.set(promoCode, discount.get("description"));
        }
      });
    });

    return discountDescriptionMap;
  }
);
